import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import type { components } from "../schema";

export type Scene = components["schemas"]["Scene"];

export type UseWorldlineScenesResult = UseQueryResult<Scene[], ApiError>;

// GET /worldlines/{worldlineId}/scenes を呼び、シーン一覧（createdAt 昇順）を返す query。
// worldlineId が null の場合はクエリ自体を無効化する。
export function useWorldlineScenes(
  worldlineId: string | null,
): UseWorldlineScenesResult {
  const client = useApiClient();

  return useQuery<Scene[], ApiError>({
    queryKey: ["worldlineScenes", worldlineId],
    enabled: worldlineId !== null,
    queryFn: async () => {
      // enabled: false の場合は呼ばれないが、型を狭めるための保険的なガード。
      if (worldlineId === null) {
        throw new ApiError(0, {
          type: "about:blank",
          title: "Invalid state",
          status: 0,
          detail: "useWorldlineScenes queryFn invoked without worldlineId",
        });
      }

      const { data, error } = await client.GET(
        "/worldlines/{worldlineId}/scenes",
        {
          params: { path: { worldlineId } },
        },
      );

      if (error !== undefined) {
        throw new ApiError(
          typeof (error as { status?: number }).status === "number"
            ? (error as { status: number }).status
            : 0,
          error as unknown as ApiError["problem"],
        );
      }
      if (data === undefined) {
        throw new ApiError(500, {
          type: "about:blank",
          title: "Empty response",
          status: 500,
          detail: "listWorldlineScenes returned no data",
        });
      }
      return data;
    },
  });
}
